/**
 * useIndustryProfilesList — fetches the global published industry
 * profiles for use in any "pick the industry" dropdown. Replaces the
 * hardcoded enums that used to ship in OnboardingWizard,
 * OrgSettingsPanel, LiveRecalcPanel and OrgListView.
 *
 * Returns ``{ industries, byId, loading, error }`` where:
 *   - ``industries`` is an array of ``{ id, name, short_label }``
 *     sorted alphabetically by name (same shape the dropdowns expect).
 *   - ``byId`` is a stable lookup map for label resolution
 *     (e.g. ``byId['biotech']?.name`` when rendering chips).
 *
 * Fetches once on mount, no auth required beyond the existing session
 * cookie/Bearer token. Falls back gracefully to an empty list if the
 * endpoint is unreachable so dropdowns never render `undefined`.
 */
import { useEffect, useMemo, useState } from 'react';
import authedFetch from '../lib/authedFetch';
import logger from '../lib/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function useIndustryProfilesList() {
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    (async () => {
      try {
        const res = await authedFetch(`${API_URL}/api/industry-profiles`);
        if (!mounted) return;
        if (!res.ok) throw new Error(res.statusText);
        const data = await res.json();
        const list = (data?.profiles || [])
          .map((p) => ({ id: p.id, name: p.name, short_label: p.short_label || p.name }))
          .sort((a, b) => a.name.localeCompare(b.name));
        setIndustries(list);
      } catch (e) {
        logger.warn('useIndustryProfilesList fetch failed', e);
        if (mounted) setError(e);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const byId = useMemo(() => {
    const m = {};
    for (const i of industries) m[i.id] = i;
    return m;
  }, [industries]);

  return { industries, byId, loading, error };
}
